<template>
    <AppLayout title="Revisi Penyusunan Dokumen Pengadaan">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Revisi Penyusunan Dokumen Pengadaan
            </h2>
        </template>

        <div class="py-12">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
                    <div class="p-6">
                        <!-- Header Section -->
                        <div class="flex justify-between items-center mb-6">
                            <div>
                                <Button 
                                    icon="pi pi-arrow-left" 
                                    label="Kembali" 
                                    class="p-button p-button-secondary mb-4" 
                                    @click="goBack" 
                                />
                                <h3 class="text-lg font-medium text-gray-900">
                                    Daftar Dokumen Pengadaan Yang Perlu Revisi
                                </h3>
                                <p class="mt-1 text-sm text-gray-600">
                                    Dokumen pada tahap penyusunan dokumen pengadaan yang memerlukan revisi
                                </p>
                            </div>
                        </div>

                        <!-- Data Table -->
                        <DataTable
                            :value="documents"
                            :paginator="true"
                            :rows="pageSize"
                            :totalRecords="totalRecords"
                            :lazy="true"
                            :loading="loading"
                            @page="onPage($event)"
                            @sort="onSort($event)"
                            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                            :rowsPerPageOptions="[5, 10, 25]"
                            currentPageReportTemplate="Menampilkan {first} sampai {last} dari {totalRecords} data"
                            responsiveLayout="scroll"
                            :globalFilterFields="['paket_pengadaan.nama_paket_pengadaan']"
                            class="p-datatable-sm"
                        >
                            <template #header>
                                <div class="flex justify-between items-center">
                                    <div class="p-input-icon-left">
                                        <i class="pi pi-search" />
                                        <InputText
                                            v-model="filters.global"
                                            placeholder="Cari nama paket..."
                                            @input="onSearch"
                                            class="p-inputtext-sm"
                                        />
                                    </div>
                                </div>
                            </template>

                            <template #empty>
                                <div class="text-center py-4">
                                    <i class="pi pi-info-circle text-gray-400 text-4xl mb-4"></i>
                                    <p class="text-gray-500">Tidak ada dokumen pengadaan yang perlu revisi</p>
                                </div>
                            </template>

                            <Column field="paket_pengadaan.nama_paket_pengadaan" header="Nama Paket Pengadaan" sortable>
                                <template #body="slotProps">
                                    <div class="font-medium">{{ slotProps.data.paket_pengadaan?.nama_paket_pengadaan || '-' }}</div>
                                    <div class="text-sm text-gray-500">{{ slotProps.data.uuid }}</div>
                                </template>
                            </Column>

                            <Column field="tahap.nama_tahap" header="Tahap" sortable>
                                <template #body="slotProps">
                                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {{ slotProps.data.tahap?.nama_tahap || 'Penyusunan Dokumen Pengadaan' }}
                                    </span>
                                </template>
                            </Column>

                            <Column field="status" header="Status" sortable>
                                <template #body="slotProps">
                                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        {{ slotProps.data.status.toUpperCase() }}
                                    </span>
                                </template>
                            </Column>

                            <Column field="total_hps" header="Total HPS" sortable>
                                <template #body="slotProps">
                                    <div class="font-medium">{{ formatCurrency(slotProps.data.total_hps) }}</div>
                                </template>
                            </Column>

                            <Column field="created_at" header="Dibuat" sortable>
                                <template #body="slotProps">
                                    <div class="text-sm">{{ formatDate(slotProps.data.created_at) }}</div>
                                </template>
                            </Column>

                            <Column field="updated_at" header="Diperbarui" sortable>
                                <template #body="slotProps">
                                    <div class="text-sm">{{ formatDate(slotProps.data.updated_at) }}</div>
                                </template>
                            </Column>

                            <Column header="Aksi" :exportable="false" style="min-width: 120px">
                                <template #body="slotProps">
                                    <div class="flex gap-2">
                                        <Button
                                            icon="pi pi-eye"
                                            class="p-button-rounded p-button-text p-button-info p-button-sm"
                                            @click="viewDetail(slotProps.data)"
                                            v-tooltip.top="'Lihat Detail'"
                                        />
                                        <Button
                                            icon="pi pi-pencil"
                                            class="p-button-rounded p-button-text p-button-warning p-button-sm"
                                            @click="revisiDocument(slotProps.data)"
                                            v-tooltip.top="'Revisi'"
                                        />
                                        <!-- Cancel Package Button - Only show for PPK -->
                                        <Button
                                            v-if="isPPK"
                                            icon="pi pi-ban"
                                            class="p-button-rounded p-button-text p-button-danger p-button-sm"
                                            @click="showCancelDialog(slotProps.data)"
                                            v-tooltip.top="'Batalkan Paket'"
                                        />
                                    </div>
                                </template>
                            </Column>
                        </DataTable>
                    </div>
                </div>
            </div>
        </div>

        <!-- Cancel Package Confirmation Dialog -->
        <Dialog 
            v-model:visible="cancelDialog.visible" 
            :style="{width: '450px'}" 
            header="Konfirmasi Pembatalan Paket" 
            :modal="true"
            :closable="false"
        >
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem; color: #ff6b35;" />
                <span>
                    <p class="mb-4">Apakah Anda yakin ingin membatalkan paket pengadaan berikut?</p>
                    <div v-if="cancelDialog.document" class="mb-4 p-3 bg-gray-50 rounded">
                        <h4 class="font-medium text-gray-900">{{ cancelDialog.document.paket_pengadaan?.nama_paket_pengadaan }}</h4>
                        <p class="text-sm text-gray-600">UUID: {{ cancelDialog.document.uuid }}</p>
                    </div>
                    <p class="mb-4 text-sm text-red-600">
                        <strong>Perhatian:</strong> Tindakan ini akan mengubah status paket menjadi <strong>"DITOLAK"</strong> dan tidak dapat dibatalkan.
                    </p>
                </span>
            </div>
            
            <div class="mt-4">
                <label for="cancel_reason" class="block text-sm font-medium text-gray-700 mb-2">
                    Alasan Pembatalan <span class="text-red-500">*</span>
                </label>
                <Textarea 
                    id="cancel_reason"
                    v-model="cancelDialog.reason" 
                    rows="4" 
                    cols="50" 
                    placeholder="Jelaskan alasan pembatalan paket pengadaan..."
                    class="w-full"
                    :class="{ 'p-invalid': cancelDialog.reasonError }"
                />
                <small v-if="cancelDialog.reasonError" class="p-error">{{ cancelDialog.reasonError }}</small>
            </div>
            
            <template #footer>
                <Button 
                    label="Batal" 
                    icon="pi pi-times" 
                    @click="closeCancelDialog" 
                    class="p-button-text" 
                    :disabled="cancelDialog.processing"
                />
                <Button 
                    label="Ya, Batalkan Paket" 
                    icon="pi pi-check" 
                    @click="cancelPackage" 
                    class="p-button-danger"
                    :loading="cancelDialog.processing"
                    :disabled="!cancelDialog.reason.trim()"
                />
            </template>
        </Dialog>
    </AppLayout>
</template>

<script>
import { defineComponent } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Dialog from 'primevue/dialog'
import Textarea from 'primevue/textarea'
import axios from 'axios'

export default defineComponent({
    name: 'RevisiPenyusunanDokumenPengadaan',
    components: {
        AppLayout,
        DataTable,
        Column,
        Button,
        InputText,
        Dialog,
        Textarea
    },
    data() {
        return {
            documents: [],
            loading: false,
            totalRecords: 0,
            pageSize: 10,
            first: 0,
            sortField: null,
            sortOrder: null,
            filters: {
                global: ''
            },
            searchTimeout: null,
            cancelDialog: {
                visible: false,
                document: null,
                reason: '',
                reasonError: '',
                processing: false
            }
        }
    },
    computed: {
        isPPK() {
            return this.$page.props.session_role?.role?.name?.toLowerCase() === 'ppk'
        }
    },
    mounted() {
        this.loadDocuments()
    },
    methods: {
        goBack() {
            this.$inertia.visit('/dokumen_pengadaan/dashboard');
        },
        async loadDocuments() {
            this.loading = true
            try {
                const response = await axios.get(
                    route('dokumen_pengadaan.list_revisi_penyusunan_dokumen_pengadaan.data') + 
                    this.buildQueryString()
                )
                
                if (response.data.success) {
                    this.documents = response.data.payload.data || []
                    this.totalRecords = response.data.payload.total || 0
                } else {
                    const errorMessage = response.data.error || response.data.message || 'Gagal memuat data dokumen pengadaan'
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: errorMessage,
                        life: 3000
                    })
                }
            } catch (error) {
                console.error('Error loading documents:', error)
                let errorMessage = 'Terjadi kesalahan saat memuat data'
                
                if (error.response) {
                    // Server responded with error status
                    const status = error.response.status
                    if (status === 403) {
                        errorMessage = 'Anda tidak memiliki akses untuk melihat data ini'
                    } else if (status === 401) {
                        errorMessage = 'Sesi Anda telah berakhir, silakan login kembali'
                    } else if (error.response.data?.error) {
                        errorMessage = error.response.data.error
                    }
                } else if (error.request) {
                    // Network error
                    errorMessage = 'Koneksi bermasalah, silakan coba lagi'
                }
                
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: errorMessage,
                    life: 3000
                })
            } finally {
                this.loading = false
            }
        },

        buildQueryString() {
            const params = new URLSearchParams()
            
            params.append('page', Math.floor(this.first / this.pageSize) + 1)
            params.append('per_page', this.pageSize)
            
            if (this.sortField) {
                params.append('sort', this.sortField)
                params.append('direction', this.sortOrder === 1 ? 'asc' : 'desc')
            }
            
            if (this.filters.global) {
                params.append('search', this.filters.global)
            }
            
            return '?' + params.toString()
        },

        onPage(event) {
            this.first = event.first
            this.pageSize = event.rows
            this.loadDocuments()
        },

        onSort(event) {
            this.sortField = event.sortField
            this.sortOrder = event.sortOrder
            this.loadDocuments()
        },

        onSearch() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
            }
            
            this.searchTimeout = setTimeout(() => {
                this.first = 0 // Reset to first page
                this.loadDocuments()
            }, 500)
        },

        viewDetail(document) {
            // Debug: Check what data is available
            console.log('Document data:', document)
            console.log('Document ID:', document.id)
            console.log('Document UUID:', document.uuid)
            
            // Navigate to detail page using existing view detail route  
            this.$inertia.visit(route('dokumen_pengadaan.view_detail', document.id))
        },

        revisiDocument(document) {
            this.$inertia.visit(route('dokumen_pengadaan.revisi_penyusunan_dokumen_pengadaan', document.uuid))
        },

        formatCurrency(value) {
            if (value === null || value === undefined) return 'Rp 0'
            return new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(value)
        },

        formatDate(dateString) {
            if (!dateString) return '-'
            return new Date(dateString).toLocaleDateString('id-ID', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        },

        showCancelDialog(document) {
            this.cancelDialog.visible = true
            this.cancelDialog.document = document
            this.cancelDialog.reason = ''
            this.cancelDialog.reasonError = ''
        },

        closeCancelDialog() {
            this.cancelDialog.visible = false
            this.cancelDialog.document = null
            this.cancelDialog.reason = ''
            this.cancelDialog.reasonError = ''
            this.cancelDialog.processing = false
        },

        async cancelPackage() {
            // Validate reason
            if (!this.cancelDialog.reason.trim()) {
                this.cancelDialog.reasonError = 'Alasan pembatalan wajib diisi'
                return
            }

            if (this.cancelDialog.reason.trim().length < 10) {
                this.cancelDialog.reasonError = 'Alasan pembatalan minimal 10 karakter'
                return
            }

            this.cancelDialog.reasonError = ''
            this.cancelDialog.processing = true

            try {
                await axios.post(route('dokumen_pengadaan.batalkan_paket'), {
                    uuid: this.cancelDialog.document.uuid,
                    alasan_penolakan: this.cancelDialog.reason.trim()
                })

                this.$toast.add({
                    severity: 'success',
                    summary: 'Berhasil',
                    detail: 'Paket pengadaan berhasil dibatalkan',
                    life: 3000
                })

                // Close dialog and refresh data
                this.closeCancelDialog()
                this.loadDocuments()

            } catch (error) {
                console.error('Error canceling package:', error)
                
                let errorMessage = 'Terjadi kesalahan saat membatalkan paket'
                if (error.response?.status === 403) {
                    errorMessage = 'Anda tidak memiliki akses untuk membatalkan paket ini'
                } else if (error.response?.status === 401) {
                    errorMessage = 'Sesi Anda telah berakhir. Silakan login ulang'
                } else if (error.response?.status === 422) {
                    errorMessage = error.response.data?.message || 'Data tidak valid'
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message
                }

                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: errorMessage,
                    life: 5000
                })
            } finally {
                this.cancelDialog.processing = false
            }
        }
    }
})
</script> 